import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MdOutlineElectricBolt, MdSearch, MdKeyboardArrowDown } from "react-icons/md";
import { baseurl } from "../../baseurl";

const ElectricityCheck = ({ setPayload, loading: parentLoading }) => {
  const [billers, setBillers] = useState([]);
  const [filteredBillers, setFilteredBillers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBiller, setSelectedBiller] = useState(null);
  
  const [billerParams, setBillerParams] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [fetchingParams, setFetchingParams] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const dropdownRef = useRef(null);

  // Load Billers
  useEffect(() => {
    const fetchBillers = async () => {
      try {
        const res = await axios.get(`${baseurl}/getBbpsBillersByCategory/electricity`);
        const data = res.data.data?.billers || [];
        setBillers(data);
        setFilteredBillers(data);
      } catch (err) {
        console.error("Failed to load providers", err);
      }
    };
    fetchBillers();
  }, []);

  // Filter Logic
  useEffect(() => {
    const filtered = billers.filter(biller =>
      biller.billerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBillers(filtered);
  }, [searchTerm, billers]);

  // Click Outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Biller Fields
  const handleSelectBiller = async (biller) => {
    setSelectedBiller(biller);
    setSearchTerm("");
    setIsOpen(false);
    setBillerParams([]);
    setFormValues({});
    if (setPayload) setPayload(null);

    setFetchingParams(true);
    try {
      const res = await axios.get(`${baseurl}/getBbpsBillerById/${biller.billerId}`);
      const params = res.data.data?.biller?.[0]?.customerParams;
      setBillerParams(Array.isArray(params) ? params : params ? [params] : []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetchingParams(false);
    }
  };

  // Update Payload and notify parent
  const handleInputChange = (name, value) => {
    const updated = { ...formValues, [name]: value };
    setFormValues(updated);

    const currentPayload = {
      biller_id: selectedBiller?.billerId,
      customerParams: Object.keys(updated).map(k => ({ name: k, value: updated[k] }))
    };

    if (setPayload) {
      setPayload(currentPayload);
    }
  };

  /**
   * API CALL: Fetch Bill Details
   */
const handleFetchBill = async (e) => {
  e.preventDefault();
  if (!selectedBiller) return alert("Please select a board");

  // Construct the payload for the API call
  const payload = {
    biller_id: selectedBiller.billerId,
    customerParams: Object.keys(formValues).map(k => ({ 
      name: k, 
      value: formValues[k] 
    }))
  };

  setIsProcessing(true);
  try {
    const response = await axios.post(`${baseurl}/fetchElectricityBillDetails`, payload);
    
    if (response.data.status === "SUCCESS" || response.data.data) {
      console.log("Bill Details:", response.data);

      // MERGE biller_id and customerParams into the data object 
      // so the next page has everything it needs for the pay API
      const dataToPass = {
        ...response.data.data,
        biller_id: payload.biller_id,
        customerParams: payload.customerParams
      };

      const dataStr = encodeURIComponent(JSON.stringify(dataToPass));
      
      // Navigate to the details page with the enriched data
      window.location.href = `/bill-details?data=${dataStr}`;
    } else {
      alert(response.data.message || "Unable to fetch bill details");
    }
  } catch (err) {
    console.error("API Error:", err);
    alert("Error fetching bill. Please verify your Consumer Number.");
  } finally {
    setIsProcessing(false);
  }
};

  return (
    <div className="electricity-check-container animate-fade-in">
      <form onSubmit={handleFetchBill}>
        <label className="fw-bold small mb-2 text-dark">Select Electricity Board</label>
        
        <div className="custom-select-wrapper mb-4" ref={dropdownRef}>
          <div 
            className={`custom-select-header ${isOpen ? 'active' : ''}`} 
            onClick={() => !parentLoading && setIsOpen(!isOpen)}
          >
            <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
              <MdOutlineElectricBolt className="me-2 flex-shrink-0" style={{ color: "#166070", fontSize: "20px" }} />
              <span className={`text-truncate ${!selectedBiller ? "text-muted" : "text-dark fw-medium"}`}>
                {selectedBiller ? selectedBiller.billerName : "Search or Select Board"}
              </span>
            </div>
            <MdKeyboardArrowDown className={`arrow-icon ${isOpen ? 'rotated' : ''}`} />
          </div>

          {isOpen && (
            <div className="custom-select-dropdown shadow-lg animate-slide-down">
              <div className="search-box-wrapper sticky-top bg-white">
                <MdSearch className="search-icon" />
                <input 
                  type="text" 
                  className="search-input" 
                  placeholder="Type to search board..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="options-list">
                {filteredBillers.length > 0 ? (
                  filteredBillers.map((biller) => (
                    <div 
                      key={biller.id} 
                      className={`option-item ${selectedBiller?.id === biller.id ? 'selected' : ''}`}
                      onClick={() => handleSelectBiller(biller)}
                    >
                      {biller.billerName}
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-center text-muted x-small">No boards found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {fetchingParams && (
          <div className="text-center my-4 py-2 bg-light rounded-3 border">
            <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
            <span className="ms-2 x-small text-muted fw-bold">LOADING REQUIREMENTS...</span>
          </div>
        )}

        <div className="row g-3">
          {billerParams.map((param, index) => (
            <div className="col-12" key={index}>
              <label className="fw-bold x-small text-uppercase text-muted mb-1">{param.paramName}</label>
              <input
                type={param.dataType === "NUMERIC" ? "number" : "text"}
                className="form-control shadow-none py-2"
                placeholder={`Enter ${param.paramName}`}
                value={formValues[param.paramName] || ""}
                onChange={(e) => handleInputChange(param.paramName, e.target.value)}
                disabled={parentLoading || isProcessing}
                required
              />
            </div>
          ))}
        </div>

        {selectedBiller && !fetchingParams && (
          <button 
            type="submit" 
            className="btn w-100 mt-4 py-2 text-white fw-bold shadow-sm"
            style={{ background: "#166070", borderRadius: "8px" }}
            disabled={isProcessing || parentLoading}
          >
            {isProcessing ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Checking...</>
            ) : "Fetch Bill Details"}
          </button>
        )}
      </form>

      <style>{`
        .custom-select-wrapper { position: relative; width: 100%; }
        .custom-select-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 15px;
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-height: 48px;
        }
        .custom-select-header:hover { border-color: #166070; }
        .custom-select-header.active { border-color: #166070; box-shadow: 0 0 0 3px rgba(22, 96, 112, 0.1); }
        .arrow-icon { transition: transform 0.3s ease; color: #6c757d; flex-shrink: 0; }
        .arrow-icon.rotated { transform: rotate(180deg); }
        .custom-select-dropdown {
          position: absolute;
          top: calc(100% + 5px);
          left: 0;
          right: 0;
          background: #fff;
          border-radius: 10px;
          z-index: 9999;
          border: 1px solid #eee;
          max-height: 40vh; 
          display: flex;
          flex-direction: column;
        }
        .search-box-wrapper {
          display: flex;
          align-items: center;
          padding: 10px 15px;
          border-bottom: 1px solid #f0f0f0;
          flex-shrink: 0;
        }
        .search-input { border: none; outline: none; width: 100%; font-size: 0.9rem; }
        .options-list { overflow-y: auto; flex-grow: 1; }
        .option-item {
          padding: 12px 15px;
          font-size: 0.88rem;
          cursor: pointer;
          color: #444;
        }
        .option-item:hover { background: #f0f7f9; color: #166070; }
        .option-item.selected { background: #166070; color: #fff; }
        .x-small { font-size: 11px; }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-down { animation: slideDown 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default ElectricityCheck;